/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IAllowableFreightViewProps } from './allowable-freight';
import { Progress } from 'reactstrap';
import { AllowableFreightProgressBarClassEnum } from './utils/AllowableFreightProgressBarEnum';
import { IPromotionsData } from './allowable-freight.props.autogenerated';

export default (props: IAllowableFreightViewProps) => {
    const {
        data: { cart },
        config: { requireCouponForPromoBar, promotions },
        state: { maxLimit, limitReached, mustShipLTL, oversizedItem, containsOil, errorMessage, cartFreight }
    } = props;
    const minimumRange = 0;

    const getTickerColourClass = () => {
        //works on a priority system
        if (mustShipLTL) {
            return AllowableFreightProgressBarClassEnum.Red;
        }
        if (oversizedItem || containsOil) {
            return AllowableFreightProgressBarClassEnum.Yellow;
        }
        if (limitReached) {
            return AllowableFreightProgressBarClassEnum.Green;
        }
        return AllowableFreightProgressBarClassEnum.Red;
    };

    const shouldRenderPromoBars = () => {
        if (requireCouponForPromoBar && props.state.cartHasSpringPromoCoupon) {
            return true;
        } else if (!requireCouponForPromoBar) {
            return true;
        }
        return false;
    };
    const getValueForLabel = (value: string | number) => {
        const formattedValue = !props.state.isUnitCount ? props.context.cultureFormatter.formatCurrency(value) : value;
        return formattedValue.toString();
    };

    const getProductCodeByItemId = (itemId: string) => props.state.productCodes.find(pc => pc.ItemId === itemId)?.ProductCode || null;

    const getMaxPromoAmount = (promotion: IPromotionsData) => Math.max(...promotion.promoBreakpoints.map(pr => pr.spendAmount));
    const getAmountForMfr = (promotion: IPromotionsData) => {
        const mfr = promotion.mfr;
        if (mfr === '*') {
            const total = (cart.result?.cart.CartLines || []).map(cl => cl.TotalAmount).reduce((tot, acc) => tot! + acc!) || 0;
            console.log('Total', total);
            return total;
        } else {
            let cartLines = cart.result?.cart.CartLines?.filter(cl => cl.ItemId?.startsWith(mfr)) || [];
            const { inclusionCodes, exclusionCodes } = promotion;
            if (inclusionCodes && inclusionCodes.length) {
                cartLines = cartLines.filter(cl => {
                    const productCode = getProductCodeByItemId(cl.ItemId || '');
                    if (productCode) {
                        return inclusionCodes.includes(productCode);
                    }
                    return true;
                });
            } else if (exclusionCodes) {
                cartLines = cartLines.filter(cl => !exclusionCodes.includes(getProductCodeByItemId(cl.ItemId || '') || ''));
            }
            const amounts = cartLines.map(cl => cl.TotalAmount);
            if (Array.isArray(amounts) && !!amounts.length) {
                const total = amounts.reduce((tot, acc) => tot! + acc!) || 0;
                return total;
            }
        }
        return 0;
    };

    const getNextTier = (promo: IPromotionsData) => {
        let tierIdx = 0;
        let tier = promo.promoBreakpoints[tierIdx];
        const totalForMfr = getAmountForMfr(promo);
        // eslint-disable-next-line no-constant-condition
        while (true) {
            if (tier.spendAmount > totalForMfr) {
                return tier;
            }
            tierIdx++;
            const _tier = promo.promoBreakpoints[tierIdx];
            if (_tier) {
                tier = _tier;
            } else {
                break;
            }
        }
        return tier;
    };

    if (!props.state.cartFreight && !props.state.maxLimit) {
        return null;
    }
    React.useEffect(() => {
        if (cart.result?.cart.CartLines?.length === 0) {
            props.state.cartFreight = 0;
        }
    }, [cart.result?.cart.CartLines?.length]);

    return (
        (shouldRenderPromoBars() ||
            (!props.state.cartHasSpringPromoCoupon && cart.result?.cart.CartLines?.length! > 0 && !cart.result?.hasInvoiceLine)) && (
            <div className='row ms-allowable-freight ms-allowable-freight__heading'>
                {!props.state.cartHasSpringPromoCoupon && cart.result?.cart.CartLines?.length! > 0 ? (
                    <div className='col-12'>
                        <h4 className='ms-allowable-freight__label'>{`${props.resources.sliderLabel}: ${getValueForLabel(
                            cartFreight
                        )}`}</h4>
                        <div className='row'>
                            <span className='range-text'>{getValueForLabel(minimumRange)}</span>
                            <Progress
                                className={`ms-allowable-freight__slider ${getTickerColourClass()}`}
                                min={minimumRange}
                                max={maxLimit}
                                value={Math.min(cartFreight, maxLimit)}
                            />
                            <span className='range-text'>{getValueForLabel(maxLimit)}</span>
                        </div>
                        {errorMessage && <div className='ms-allowable-freight__message'>{errorMessage}</div>}
                    </div>
                ) : null}

                {shouldRenderPromoBars() &&
                    promotions?.map((promo, i) => {
                        const maxPromoAmount = getMaxPromoAmount(promo);
                        const total = getAmountForMfr(promo);
                        if (maxPromoAmount === 0 || total === 0) {
                            return null;
                        }
                        const tier = getNextTier(promo);

                        let headerText = `Spend ${props.context.cultureFormatter.formatCurrency(tier.spendAmount - total)} to ${
                            tier.savingText
                        }`;
                        if (total >= maxPromoAmount) {
                            headerText = `${tier.savingText}!`;
                        }
                        const valueMax = promo.promoBreakpoints.map(bp => bp.spendAmount).reduce((tot, acc) => tot + acc);

                        return (
                            <div className='col-12 gdn-promobar' key={`${promo.mfr}-${i}`} style={{ marginBottom: '1rem' }}>
                                <h4 className='gdn-promobar__header'>
                                    <span className={'gdn-promobar__spend'}>
                                        Current {promo.mfr === '*' ? '' : promo.label || promo.mfr} spend:{' '}
                                        {props.context.cultureFormatter.formatCurrency(total)}
                                    </span>
                                    <span className={'gdn-promobar__save'}>{headerText} </span>
                                </h4>
                                <div className='row'>
                                    <span className='range-text promo-label'>{promo.label}</span>
                                    <Progress
                                        className={``}
                                        multi={true}
                                        max={maxPromoAmount}
                                        style={{
                                            display: 'flex',
                                            flexGrow: 1,
                                            margin: '0 2%',
                                            height: '20px',
                                            borderRadius: '10px',
                                            backgroundColor: 'lightgrey'
                                        }}
                                    >
                                        {promo.promoBreakpoints.map((tier, idx) => {
                                            let className = 'gardner-red-promo';
                                            if (total >= tier.spendAmount) {
                                                className = `gardner-green-promo`;
                                            }
                                            if (idx > 0) {
                                                const prevTier = promo.promoBreakpoints[idx - 1];
                                                if (prevTier.spendAmount >= total) {
                                                    return null;
                                                }
                                            }
                                            return (
                                                <Progress
                                                    bar
                                                    value={Math.min(total, tier.spendAmount)}
                                                    max={valueMax}
                                                    key={tier.spendAmount}
                                                    className={`${className} ${idx === 0 ? 'promo-left-radius' : ''} ${
                                                        idx === promo.promoBreakpoints.length - 1 ? 'promo-right-radius' : ''
                                                    }`}
                                                >
                                                    <span style={{ color: 'lightgrey' }}>
                                                        {tier.spendAmount <= total
                                                            ? tier.savingText
                                                            : props.context.cultureFormatter.formatCurrency(total)}
                                                    </span>
                                                </Progress>
                                            );
                                        })}
                                    </Progress>
                                    <span className='range-text promo-label'>
                                        {props.context.cultureFormatter.formatCurrency(getMaxPromoAmount(promo)).replace('.00', '')}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
            </div>
        )
    );
};
